import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { NATURAL_TEXTURES } from '../config/images';
import { X, Palette, Paintbrush, Home, Shield, ZoomIn } from 'lucide-react';

// Color data for the dots with texture images
const heroColors = [
  { id: 'betonite', hex: '#C5C5C0', nameHe: 'בטונייט', nameEn: 'Betonite', texture: NATURAL_TEXTURES.betonite },
  { id: 'betonda', hex: '#6B6B6B', nameHe: 'בטונדה', nameEn: 'Betonda', texture: NATURAL_TEXTURES.betonda },
  { id: 'mika', hex: '#D4C4A8', nameHe: 'מיקה', nameEn: 'Mika', texture: NATURAL_TEXTURES.mika },
  { id: 'titanium', hex: '#F5F5F0', nameHe: 'טיטניום', nameEn: 'Titanium', texture: NATURAL_TEXTURES.titanium },
  { id: 'clay', hex: '#C67B5C', nameHe: 'חרסית', nameEn: 'Clay', texture: NATURAL_TEXTURES.clay },
  { id: 'yatir', hex: '#D4A060', nameHe: 'יתיר', nameEn: 'Yatir', texture: NATURAL_TEXTURES.yatir },
];

// Floating Bubbles Animation Component - Improved
const FloatingBubbles = () => {
  const [escapeBubble, setEscapeBubble] = useState(null);

  // Generate random bubbles - smaller and more subtle
  const bubbles = useMemo(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      id: i,
      size: Math.random() * 25 + 8, // 8-33px (smaller)
      left: Math.random() * 100,
      delay: Math.random() * 10,
      duration: Math.random() * 12 + 10, // 10-22s
      opacity: Math.random() * 0.25 + 0.05, // 0.05-0.3 (more subtle)
      color: heroColors[Math.floor(Math.random() * heroColors.length)].hex,
      drift: (Math.random() - 0.5) * 80, // -40 to 40px horizontal drift
    }));
  }, []);

  // Every 15 seconds, create an escape bubble
  useEffect(() => {
    const interval = setInterval(() => {
      const randomColor = heroColors[Math.floor(Math.random() * heroColors.length)];
      setEscapeBubble({
        id: Date.now(),
        size: Math.random() * 20 + 15,
        left: Math.random() * 80 + 10,
        color: randomColor.hex,
      });
      
      // Clear after animation completes
      setTimeout(() => setEscapeBubble(null), 8000);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Regular bubbles in hero */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {bubbles.map((bubble) => (
          <motion.div
            key={bubble.id}
            className="absolute rounded-full"
            style={{
              width: bubble.size,
              height: bubble.size,
              left: `${bubble.left}%`,
              background: `radial-gradient(circle at 30% 30%, ${bubble.color}50, ${bubble.color}15)`,
              border: `1px solid ${bubble.color}25`,
              boxShadow: `inset 0 0 ${bubble.size/3}px ${bubble.color}30`,
            }}
            initial={{ 
              top: '-5%',
              opacity: 0,
              scale: 0.3,
            }}
            animate={{ 
              top: '105%',
              opacity: [0, bubble.opacity, bubble.opacity * 0.8, 0],
              scale: [0.3, 1, 0.9, 0.6],
              x: [0, bubble.drift, bubble.drift * 0.5, bubble.drift * 1.2],
            }}
            transition={{
              duration: bubble.duration,
              delay: bubble.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Escape bubble - travels to bottom of page */}
      <AnimatePresence>
        {escapeBubble && (
          <motion.div
            key={escapeBubble.id}
            className="fixed rounded-full pointer-events-none z-50"
            style={{
              width: escapeBubble.size,
              height: escapeBubble.size,
              left: `${escapeBubble.left}%`,
              background: `radial-gradient(circle at 30% 30%, ${escapeBubble.color}60, ${escapeBubble.color}20)`,
              border: `1px solid ${escapeBubble.color}40`,
              boxShadow: `0 0 20px ${escapeBubble.color}30, inset 0 0 10px ${escapeBubble.color}40`,
            }}
            initial={{ 
              top: '30%',
              opacity: 0,
              scale: 0.5,
            }}
            animate={{ 
              top: '95vh',
              opacity: [0, 0.6, 0.5, 0.3, 0],
              scale: [0.5, 1, 0.9, 0.7, 0.3],
              x: [0, 30, -20, 40, 0],
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{
              duration: 7,
              ease: "easeInOut",
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Color preview popup on long press - with zoom functionality
const ColorPreviewPopup = ({ isOpen, onClose, isRTL }) => {
  const [zoomedColor, setZoomedColor] = useState(null);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />
      
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25 }}
        className="relative bg-card/95 backdrop-blur-xl rounded-3xl p-6 max-w-md w-full shadow-2xl border border-border/50"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 end-4 w-8 h-8 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
          {isRTL ? 'ציפויים מינרליים טבעיים' : 'Natural Mineral Coatings'}
        </h3>

        <div className="grid grid-cols-3 gap-4">
          {heroColors.map((color, index) => (
            <motion.button
              key={color.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.05 }}
              className="flex flex-col items-center gap-2 group cursor-pointer"
              onClick={() => setZoomedColor(color)}
            >
              <div className="w-16 h-16 rounded-xl shadow-lg border-2 border-white/30 overflow-hidden relative group-hover:scale-105 group-hover:border-accent/50 transition-all">
                <img 
                  src={color.texture} 
                  alt={color.nameHe}
                  className="w-full h-full object-cover scale-[1.8]"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </div>
              <span className="text-xs text-muted-foreground text-center">
                {isRTL ? color.nameHe : color.nameEn}
              </span>
            </motion.button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          {isRTL ? 'לחץ על צבע להגדלה' : 'Click a color to enlarge'}
        </p>
      </motion.div>

      {/* Zoomed Image Modal */}
      <AnimatePresence>
        {zoomedColor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/90"
            onClick={() => setZoomedColor(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-lg w-full"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setZoomedColor(null)}
                className="absolute -top-12 end-0 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={zoomedColor.texture} 
                  alt={zoomedColor.nameHe}
                  className="w-full aspect-square object-cover"
                />
              </div>
              
              <div className="text-center mt-4">
                <h4 className="text-xl font-bold text-white">
                  {isRTL ? zoomedColor.nameHe : zoomedColor.nameEn}
                </h4>
                <p className="text-white/60 text-sm mt-1">
                  {isRTL ? 'ציפוי מינרלי טבעי' : 'Natural Mineral Coating'}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const HeroSection = () => {
  const { t, isRTL } = useLanguage();
  const [showColorPreview, setShowColorPreview] = useState(false);

  return (
    <section className="hero-gradient text-primary-foreground relative overflow-hidden py-6 md:py-10">
      {/* Floating Bubbles Animation */}
      <FloatingBubbles />

      {/* Background Pattern - Subtle */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 end-0 w-[300px] h-[300px] rounded-full bg-gradient-radial from-accent/20 to-transparent blur-3xl" />
        <div className="absolute bottom-0 start-0 w-[200px] h-[200px] rounded-full bg-gradient-radial from-stone/15 to-transparent blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          {/* Main Title - Brand Name */}
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display leading-tight mb-2"
          >
            {isRTL ? 'מיקרוליט' : 'Microlit'}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-2xl sm:text-3xl md:text-4xl text-primary-foreground/80 mb-5"
          >
            {isRTL ? 'ציפוי מינרלי' : 'Mineral Coating'}
          </motion.p>

          {/* Description - 2 lines max */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-primary-foreground/70 max-w-xl mx-auto mb-5 leading-relaxed px-4"
          >
            {t('heroDescription')}
          </motion.p>

          {/* Since 1958 - Classic handwriting style */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-3"
          >
            <p 
              className="text-primary-foreground/50 italic"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              <span className="text-sm">since</span>
              <span className="text-2xl sm:text-3xl mx-2 text-accent font-light">1958</span>
            </p>
          </motion.div>

          {/* Feature Icons Row - More spacing, Hebrew text */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-start justify-center gap-8 sm:gap-14 md:gap-16 flex-row-reverse"
          >
            {/* 27 Textures */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center">
                <Palette className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
              </div>
              <span className="text-[11px] sm:text-xs font-medium text-primary-foreground/90 text-center leading-tight mt-1">
                {isRTL ? '27 טקסטורות' : '27 Textures'}
              </span>
            </motion.div>
            
            {/* Indoor & Outdoor */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center">
                <Home className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
              </div>
              <span className="text-[11px] sm:text-xs font-medium text-primary-foreground/90 text-center leading-tight mt-1">
                {isRTL ? 'פנים וחוץ' : 'In & Out'}
              </span>
            </motion.div>
            
            {/* DIY - Do It Yourself */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex flex-col items-center gap-1"
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center">
                <Paintbrush className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
              </div>
              <span className="text-[11px] sm:text-xs font-medium text-primary-foreground/90 text-center leading-tight mt-1 whitespace-nowrap">
                {isRTL ? 'עשה זאת בעצמך' : 'DIY'}
              </span>
            </motion.div>
          </motion.div>

          {/* Natural Mineral Color Dots - Click to preview */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-4"
          >
            {/* Click to open */}
            <motion.button 
              className="inline-flex items-center gap-2 sm:gap-3 cursor-pointer px-4 py-3 rounded-2xl bg-primary-foreground/5 hover:bg-primary-foreground/10 transition-colors border border-primary-foreground/10 relative"
              onClick={() => setShowColorPreview(true)}
              animate={{ 
                boxShadow: [
                  '0 0 0 0 rgba(255,255,255,0)',
                  '0 0 0 4px rgba(255,255,255,0.1)',
                  '0 0 0 0 rgba(255,255,255,0)'
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {heroColors.map((color, index) => (
                <motion.div
                  key={color.id}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.05, type: "spring" }}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden"
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 0 0 2px rgba(255,255,255,0.4)'
                  }}
                  whileHover={{ scale: 1.15 }}
                >
                  <img 
                    src={color.texture} 
                    alt={color.nameHe}
                    className="w-full h-full object-cover"
                    style={{ transform: 'scale(1.5)' }}
                  />
                </motion.div>
              ))}
            </motion.button>
            
            {/* Hint text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-xs text-primary-foreground/40 mt-2"
            >
              {isRTL ? 'לחץ להגדלה' : 'Click to enlarge'}
            </motion.p>
          </motion.div>
        </motion.div>
      </div>

      {/* Color Preview Popup */}
      <AnimatePresence>
        {showColorPreview && (
          <ColorPreviewPopup
            isOpen={showColorPreview}
            onClose={() => setShowColorPreview(false)}
            isRTL={isRTL}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
